import styled from "styled-components";
import { FC, memo } from "react";

import Text from "@/components/Text";
import { SectionHeading } from "@/components/Layout/Parts";
import { OptimisationProjectResource } from "@/types/resources";
import { OptimisationTestTypeEnum } from "@/utils/enums";

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  grid-column-gap: 16px;
  margin-bottom: 48px;
`;

const Box = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  padding: 15px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: 0.2s ease-out border-color;
  &:hover {
    border-color: ${({ theme }) => theme.colors.gray};
  }
`;

const HeadingWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Count = styled.div`
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  background: ${({ theme }) => theme.colors.lightGray};
`;

interface Props {
  testsCounts: OptimisationProjectResource["testsCounts"];
}

const OptimisationTestVariations: FC<Props> = memo(({ testsCounts }) => {
  const categoryTestsCount = (type: OptimisationTestTypeEnum) => {
    return testsCounts
      .filter((testCount) => testCount.type === type)
      .reduce(
        (previousValue, currentValue) => previousValue + currentValue.count,
        0
      );
  };

  const thumbTestsCount = categoryTestsCount(OptimisationTestTypeEnum.THUMBS);

  const tipsTestsCount = categoryTestsCount(OptimisationTestTypeEnum.TIPS);

  const sequencesTestsCount = categoryTestsCount(
    OptimisationTestTypeEnum.SEQUENCES
  );

  const introsTestsCount = categoryTestsCount(OptimisationTestTypeEnum.INTRO);

  const finalisationTestsCount = categoryTestsCount(
    OptimisationTestTypeEnum.FINALISATION
  );

  const titlesTestsCount = categoryTestsCount(OptimisationTestTypeEnum.TITLE);

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
    });
  };

  return (
    <>
      <SectionHeading>Variations</SectionHeading>
      <Grid>
        <Box onClick={() => scrollToSection("thumbs-section")}>
          <HeadingWrapper>
            <Text $type="smallBold">Thumbs</Text>
            <Count>
              <Text $type="smallHeadline" as="span">
                {thumbTestsCount}
              </Text>
            </Count>
          </HeadingWrapper>
        </Box>
        <Box onClick={() => scrollToSection("tips-section")}>
          <HeadingWrapper>
            <Text $type="smallBold">Tips</Text>
            <Count>
              <Text $type="smallHeadline" as="span">
                {tipsTestsCount}
              </Text>
            </Count>
          </HeadingWrapper>
        </Box>
        <Box onClick={() => scrollToSection("sequences-section")}>
          <HeadingWrapper>
            <Text $type="smallBold">Sequences</Text>
            <Count>
              <Text $type="smallHeadline" as="span">
                {sequencesTestsCount}
              </Text>
            </Count>
          </HeadingWrapper>
        </Box>
        <Box onClick={() => scrollToSection("intros-section")}>
          <HeadingWrapper>
            <Text $type="smallBold">Intros</Text>
            <Count>
              <Text $type="smallHeadline" as="span">
                {introsTestsCount}
              </Text>
            </Count>
          </HeadingWrapper>
        </Box>
        <Box onClick={() => scrollToSection("finalisation-section")}>
          <HeadingWrapper>
            <Text $type="smallBold">Finalisation</Text>
            <Count>
              <Text $type="smallHeadline" as="span">
                {finalisationTestsCount}
              </Text>
            </Count>
          </HeadingWrapper>
        </Box>
        <Box onClick={() => scrollToSection("titles-section")}>
          <HeadingWrapper>
            <Text $type="smallBold">Titles</Text>
            <Count>
              <Text $type="smallHeadline" as="span">
                {titlesTestsCount}
              </Text>
            </Count>
          </HeadingWrapper>
        </Box>
      </Grid>
    </>
  );
});

export default OptimisationTestVariations;
